import React from 'react';
import { ff } from 'helpers/featureFlags';
import { AttributeAnchors } from 'components/Anchor/Anchor';
import DebugRender from 'components/Debug/Render';
import Radio from '../Radio';
import ValidationElement from '../ValidationElement';
import { stateFormErrorConnector } from '../shared/stateFormErrors';

class RadioGroup extends ValidationElement {
  constructor(props) {
    super(props);
    this.state = {
      uid: `${props.name}-${super.guid()}`,
      error: false,
      lastSelectedValue: ''
    };
    this.onUpdate = this.onUpdate.bind(this);
    this.processError = this.processError.bind(this);
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    const selectedValue = nextProps.selectedValue;
    if (selectedValue !== this.props.selectedValue) {
      if (this.state.lastSelectedValue !== selectedValue) {
        this.processError(selectedValue);
      }
    }
  }

  componentDidMount() {
    this.onUpdate({ value: this.props.selectedValue });
  }

  /**
   * This allows us to switch logic of "hasError" between state.application and
   * state.form. When useStateFormErrors is true, we'll use
   * this.props.hasStateFormError. Otherwise, continue to use the previous error
   * handling.
   */
  hasError = () => {
    if (ff(this.props.features).getActive('EN-4733')) {
      return this.props.hasStateFormError;
    }
    return this.state.error;
  };

  processError(selectedValue) {
    const errors = this.constructor.errors.map((err) => ({
      code: err.code,
      valid: err.func(selectedValue, this.props),
      uid: this.state.uid,
    }));
    this.setState({
      error: errors.some((x) => x.valid === false),
    });
    this.props.onError(selectedValue, errors);
    this.setState({ lastSelectedValue: selectedValue });
  }

  onUpdate(option) {
    const selectedValue = option.value;
    this.processError(selectedValue);
  }

  render() {
    const { selectedValue } = this.props;
    const name = this.props.name ? `${this.state.uid}-${this.props.name}` : null;
    const children = React.Children.map(this.props.children, (child) => {
      // If type is not Radio, stop
      if (!child || child.type !== Radio) {
        return child;
      }

      // Check if current value matches one of the child radio options
      const checked = child.props.value === selectedValue;

      const onUpdate = (option) => {
        if (child.props.onUpdate) {
          child.props.onUpdate(option);
        }
        this.onUpdate(option);
      };

      return (
        <child.type
          {...child.props}
          name={name || child.props.name}
          disabled={this.props.disabled}
          checked={checked}
          onUpdate={onUpdate}
        />
      );
    });

    const errorClass = this.hasError() === true && this.props.disabled === false ? 'usa-input-error' : '';
    const classes = ['blocks', this.props.className, errorClass].join(' ').trim();
    return (
      <div className={classes} role="radiogroup">
        <AttributeAnchors attribute={this.props.attribute} />
        <DebugRender parent={this} />

        {children}
      </div>
    );
  }
}

RadioGroup.defaultProps = {
  onError: (value, arr) => arr,
  required: false,
  disabled: false,
};

RadioGroup.errors = [
  {
    code: 'required',
    func: (value, props) => {
      if (props.required) {
        return !!value;
      }
      return true;
    },
  },
];

export default stateFormErrorConnector(RadioGroup);
